import React from 'react';
import PropTypes from 'prop-types';
import {List} from 'immutable';

import {UserSelector} from 'components/pure/utils/form/UserSelector';
import connectToStores from 'alt-utils/lib/connectToStores';
import './AdminDashSearchSidebar.styl';
import AuthStore from 'stores/AuthStore';
import {browserHistory} from 'react-router';
import withCoachingPlanWizard from 'components/coaching/wizard/withCoachingPlanWizard';
import buildUrl from 'components/dashboard/buildUrl';
import Loading from 'components/pure/utils/Loading';
import UserSource from 'sources/UserSource';
import TeamListStore from 'stores/TeamListStore';
import {getCoachRepLink} from 'components/dashboard/coach/CoachSummaryDashboard';
import ManagerCoachingButton from 'components/dashboard/manager/ManagerCoachingButton';
import TeamActions from 'actions/TeamActions';
import Button from '@material-ui/core/Button';
import UpcomingReviewsStore from 'stores/coaching/UpcomingReviewsStore';
import {MY_COACHING_TAB_INDEX} from './AdminDashCoaching';
import CoachingBestPractices from '@components/coaching-best-practices/CoachingBestPractices';
import {HISTORY_ACTIONS} from 'components/StatsPage2.utils';
import {associateBy} from 'utils/associateBy';

@connectToStores
class AdminDashSearchSidebar extends React.PureComponent {
  constructor(props) {
    super(props);

    this.navigateToDashForUser = this.navigateToDashForUser.bind(this);

    this.state = {
      users: undefined,
    };
  }

  static getStores() {
    return [AuthStore, TeamListStore, UpcomingReviewsStore];
  }

  static getPropsFromStores() {
    const sessionUser = AuthStore.getState().user;
    const teams = TeamListStore.getState().teams;
    const upcomingReviews = UpcomingReviewsStore.getState().upcomingReviews;
    const upcomingReviewsCount = upcomingReviews ? upcomingReviews.size : 0;

    return {sessionUser, teams, upcomingReviewsCount};
  }

  async componentDidMount() {
    TeamActions.fetchTeamsForUser.defer();
    // why source / component state?
    // role based fetch gives us which "managers" are team managers.
    // we need to redirect non-team "managers" -> rep view.
    const users = await UserSource.fetchAll({
      roles: ['admin', 'manager', 'user'],
    });
    this.filterUsersByTeams(users.filter(u => !u.hidden));
  }

  filterUsersByTeams(usersPreFilter) {
    const usersInTeamsTheExecHasAccessTo = usersPreFilter.filter(
      // if there are no teams yet we let all users in first, we will filter again once the teams are loaded
      u =>
        this.props.teams && !this.props.teams.isEmpty() ? !!this.props.teams.get(u.teamId) : true
    );
    this.setState({users: associateBy(usersInTeamsTheExecHasAccessTo, u => u.id)});
  }

  componentDidUpdate(prevProps) {
    const prevTeamIds = prevProps.teams.keySeq().sort();
    const thisTeamIds = this.props.teams.keySeq().sort();
    if (!prevTeamIds.equals(thisTeamIds) && this.state.users) {
      this.filterUsersByTeams(this.state.users);
    }
  }

  isUserTeamManager(user) {
    const {teams} = this.props;
    const {users} = this.state;
    if (!user.isTeamManager) return false;

    // check further to see if the team(s) that this user manages has any users
    const userTeams = users.map(u => u.teamId).toSet();
    const teamsUserManages = teams.filter(t => t.managerId === user.id);
    return teamsUserManages.some(t => userTeams.has(t.id));
  }

  navigateToDashForUser(userId) {
    this.setState({loading: true}, () => {
      const {adminId, teams} = this.props;
      const {users} = this.state;
      const user = users.get(userId);
      if (!user) return;

      if (this.isUserTeamManager(user)) {
        browserHistory.push({
          state: {
            action: HISTORY_ACTIONS.PUSH,
            pathname: this.props.fromPath,
          },
          pathname: buildUrl({adminId, managerId: user.id}),
        });
      } else {
        const team = teams.get(user.teamId);
        const linkWithInfoAdded = getCoachRepLink(
          {rep: user},
          this.props,
          {},
          true,
          null,
          team.managerId
        );
        browserHistory.push(linkWithInfoAdded);
      }
    });
  }

  navigateToCoachingPlans() {
    const url =
      // Admins or non-admins with special dashboard preferences need
      // to navigate to their My Coaching tab.
      //
      // Note the presence of an adminId in props indicates whether or
      // not the destination page will have a My Coaching tab. So it
      // is used here to adjust the url.
      !!this.props.adminId
        ? `${buildUrl(this.props)}/coaching-plans?tabIndex=${MY_COACHING_TAB_INDEX}`
        : `${buildUrl(this.props)}/coaching-plans`;
    browserHistory.push(url);
  }

  showMyCoachingPlansLink() {
    return (
      // Show if user has upcoming reviews.
      this.props.upcomingReviewsCount > 0 &&
      // And is not already on the coaching plans page.
      this.props.location?.pathname.indexOf('/coaching-plans') === -1
    );
  }

  render() {
    const {sessionUser, hasRenderedDashv2Gif, showBlurb, coachId, managerId, adminId} = this.props;
    const {users} = this.state;
    if (!hasRenderedDashv2Gif) return null;
    return (
      <div className="admin-dash-search-sidebar">
        {!users ? (
          <Loading />
        ) : (
          <>
            <div className="user-search">
              <UserSelector
                placeholder="Search managers and reps"
                sessionUser={sessionUser}
                users={users}
                onChange={this.navigateToDashForUser}
              />
            </div>
            <ManagerCoachingButton
              adHocNoCallOnly={true}
              coachId={coachId}
              managerId={managerId}
              adminId={adminId}
              refreshCoachingData={this.props.refreshCoachingData}
            />
            {this.showMyCoachingPlansLink() && (
              <div className="my-coaching-plans-link">
                <Button variant="outlined" fullWidth onClick={() => this.navigateToCoachingPlans()}>
                  My Coaching Plans
                </Button>
              </div>
            )}
          </>
        )}
        {showBlurb && <CoachingBestPractices />}
      </div>
    );
  }
}

export default withCoachingPlanWizard(AdminDashSearchSidebar);

AdminDashSearchSidebar.propTypes = {
  departmentIds: PropTypes.instanceOf(List), // List<DepartmentId>
  teamId: PropTypes.string,
};
